import React, { useEffect, useRef, useState } from "react";
import { Form, Image, Select, Tabs, Upload, Result } from "antd";
import ModalComponent from "../../../../components/modals/ModalComponent.jsx";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import SignatureCanvas from "react-signature-canvas";
import { CheckKycStatusApi, UploadDocumentApi } from "../../../../api/request/kyc.js";
import { setUserDetails } from "../../../../store/slices/userSlice.js";
import LoadableButton from "../../../../components/buttons/LoadableButton.jsx";

const KycForm = () => {
  const dispatch = useDispatch();
  const { userDetails } = useSelector((state) => state.user);
  const [form] = Form.useForm();
  const sigCanvasRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [reqRejectionReason, setReqRejectionReason] = useState(null);
  const [initialValues] = useState({
    KYCDocumentType1: null,
    KYCDocument: null,
    Selfie: null,
  });
  const [documentOption] = useState([
    { value: 1, label: "Passport" },
    { value: 2, label: "National ID" },
    { value: 3, label: "Driving License" },
    { value: 4, label: "Utility Bill" },
    { value: 5, label: "Bank Statement" },
  ]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [fileList, setFileList] = useState([]);
  const [selfieFileList, setSelfieFileList] = useState([]);
  const [documentType, setDocumentType] = useState(null);

  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [signatureTab, setSignatureTab] = useState("write");
  const [signatureFile, setSignatureFile] = useState(null);
  const [signaturePreview, setSignaturePreview] = useState(null);
  const [uploadedSignatureList, setUploadedSignatureList] = useState([]);
  const [pendingFormValues, setPendingFormValues] = useState(null);

  const getBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
  };

  const handleChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
    form.setFieldsValue({ KYCDocument: newFileList });
  };

  const handleSelfieChange = ({ fileList: newSelfieList }) => {
    setSelfieFileList(newSelfieList);
    form.setFieldsValue({ Selfie: newSelfieList });
    if (newSelfieList?.length === 0) {
      form.setFieldsValue({ Selfie: null });
    }
  };

  const handleDocumentTypeChange = (value) => {
    setDocumentType(value);
  };

  const UploadButton = (
    <button type="button">
      <div className="flex justify-center">
        <img src="/assets/icons/Upload_Document_icon.svg" alt="icon" />
      </div>
      Upload
    </button>
  );

  const dataURLtoBlob = (dataURL) => {
    const arr = dataURL.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  const handleFormSubmitClick = async () => {
    try {
      const values = await form.validateFields();
      setPendingFormValues(values);
      setSignatureModalOpen(true);
    } catch {
      // form validation failed
    }
  };

  const handleSignatureConfirm = async () => {
    let finalSignatureFile = null;

    const hasDrawnSignature = sigCanvasRef.current && !sigCanvasRef.current.isEmpty();
    const hasUploadedSignature = signatureFile || uploadedSignatureList.length > 0;

    if (!hasDrawnSignature && !hasUploadedSignature) {
      toast.error("Signature is required. Please either draw or upload your signature.");
      return;
    }

    if (signatureTab === "write") {
      if (!hasDrawnSignature) {
        toast.error("Please draw your signature.");
        return;
      }
      const dataURL = sigCanvasRef.current.getCanvas().toDataURL("image/png");
      const blob = dataURLtoBlob(dataURL);
      finalSignatureFile = new File([blob], "signature.png", { type: "image/png" });
    } else {
      if (!hasUploadedSignature) {
        toast.error("Please upload your signature.");
        return;
      }
      finalSignatureFile = signatureFile || uploadedSignatureList[0]?.originFileObj;
    }

    setSignatureModalOpen(false);
    clearSignatureState();
    await submitForm(pendingFormValues, finalSignatureFile);
  };

  const submitForm = async (values, finalSignatureFile) => {
    const extractOriginFileObjs = (fileArray) => {
      return fileArray.map((file) => file.originFileObj);
    };

    const selectedDocumentType = form.getFieldValue("KYCDocumentType1");
    const requiresBothSides = selectedDocumentType === 1 || selectedDocumentType === 2;
    const uploadedDocuments = extractOriginFileObjs(values?.KYCDocument);

    if (requiresBothSides && uploadedDocuments.length !== 2) {
      toast.error("Please upload both sides of the selected document.");
      return;
    }

    setIsLoading(true);
    try {
      let formData = new FormData();
      formData.append("KYCDocumentType", values?.KYCDocumentType1);

      uploadedDocuments.forEach((file) => {
        formData.append("KYCDocument", file);
      });

      extractOriginFileObjs(values?.Selfie)?.forEach((file) => {
        formData.append("Signature", file);
      });

      formData.append("Signature", finalSignatureFile);

      let apiHeaders = {
        headers: { Accept: "application/json", "Content-Type": "multipart/form-data" },
      };
      const { data } = await UploadDocumentApi(formData, apiHeaders);

      if (data?.success) {
        form.resetFields();
        setSelfieFileList([]);
        setFileList([]);
        setSignatureFile(null);
        setSignaturePreview(null);
        setUploadedSignatureList([]);
        if (sigCanvasRef.current) sigCanvasRef.current.clear();
        dispatch(setUserDetails({ ...userDetails, status: "Pending" }));
        toast.success(data?.message);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRejectedRequest = async () => {
    try {
      let params = { UserId: userDetails?.id };
      const { data } = await CheckKycStatusApi(new URLSearchParams(params).toString());
      if (data?.success) {
        dispatch(
          setUserDetails({
            ...userDetails,
            status: data?.data?.status,
            kycStatus: data?.data?.kycStatus,
          })
        );
        setReqRejectionReason(data?.data?.comment);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (userDetails?.status === "Declined") {
      handleRejectedRequest();
    }
  }, []);

  const handleClearSignature = () => {
    if (sigCanvasRef.current) sigCanvasRef.current.clear();
  };

  const handleSignatureUploadChange = async ({ fileList: newList }) => {
    setUploadedSignatureList(newList);
    if (newList.length > 0) {
      const file = newList[0];
      setSignatureFile(file.originFileObj);
      const preview = await getBase64(file.originFileObj);
      setSignaturePreview(preview);
    } else {
      setSignatureFile(null);
      setSignaturePreview(null);
    }
  };

  const clearSignatureState = () => {
    setSignatureFile(null);
    setSignaturePreview(null);
    setUploadedSignatureList([]);
    if (sigCanvasRef.current) sigCanvasRef.current.clear();
  };

  const handleModalClose = () => {
    setSignatureModalOpen(false);
    setPendingFormValues(null);
    clearSignatureState();
    setSignatureTab("write");
  };

  const handleTabChange = (key) => {
    setSignatureTab(key);
    clearSignatureState();
  };

  const maxFiles = documentType === 1 || documentType === 2 ? 2 : 1;

  if (userDetails?.status === "Approved") {
    return (
      <Result
        status="success"
        title="Your KYC is approved successfully!"
        subTitle="Thank you for completing your verification."
      />
    );
  }

  return (
    <div className="kyc-container">
      <div className="container grid md:grid-cols-2 grid-cols-1 justify-items-center items-center gap-5 p-5 mx-auto mt-5">
        <div className="grid grid-cols-1 gap-8 w-full">
          {reqRejectionReason && (
            <div>
              <p className="font-semibold text-red-600 text-xl mb-2">
                Your KYC request is rejected because of the following reason:
              </p>
              <p
                className="text-md"
                dangerouslySetInnerHTML={{ __html: reqRejectionReason }}
              ></p>
            </div>
          )}

          <div>
            <p className="font-bold text-[#2F2B3D] sm:text-4xl text-xl">
              Please Submit Your KYC
            </p>
          </div>

          <Form
            autoComplete="off"
            form={form}
            initialValues={initialValues}
            onFinish={() => { }}
          >
            {/* Document Type */}
            <div className="max-w-[480px] ant-select-selector-white">
              <label className="text-base text-[#2F2B3DCC] font-medium">
                Document Type
              </label>
              <Form.Item
                name="KYCDocumentType1"
                rules={[{ required: true, message: "Please select a document type." }]}
              >
                <Select
                  className="w-full mt-1"
                  placeholder="Select document type"
                  options={documentOption}
                  onChange={handleDocumentTypeChange}
                />
              </Form.Item>
            </div>

            {/* Upload Document */}
            <div className="mt-5 ant-upload-drag-wrapper">
              <label className="text-base text-[#2F2B3DCC] font-medium">
                Upload Document
              </label>
              <Form.Item
                name="KYCDocument"
                rules={[{ required: true, message: "Please upload KYC documents." }]}
              >
                <Upload
                  beforeUpload={() => false}
                  listType="picture-card"
                  className="mt-1"
                  fileList={fileList}
                  onPreview={handlePreview}
                  onChange={handleChange}
                >
                  {fileList.length >= maxFiles ? null : UploadButton}
                </Upload>
              </Form.Item>
              {previewImage && (
                <Image
                  wrapperStyle={{ display: "none" }}
                  preview={{
                    visible: previewOpen,
                    onVisibleChange: (visible) => setPreviewOpen(visible),
                    afterOpenChange: (visible) => !visible && setPreviewImage(""),
                  }}
                  src={previewImage}
                />
              )}
            </div>

            {/* Upload Selfie */}
            <div className="mt-5 ant-upload-drag-wrapper">
              <label className="text-base text-[#2F2B3DCC] font-medium">
                Upload Selfie
              </label>
              <Form.Item
                name="Selfie"
                rules={[{ required: true, message: "Please upload your Selfie." }]}
              >
                <Upload
                  beforeUpload={() => false}
                  listType="picture-card"
                  className="mt-1"
                  fileList={selfieFileList}
                  onPreview={handlePreview}
                  onChange={handleSelfieChange}
                >
                  {selfieFileList.length >= 1 ? null : UploadButton}
                </Upload>
              </Form.Item>
              {previewImage && (
                <Image
                  wrapperStyle={{ display: "none" }}
                  preview={{
                    visible: previewOpen,
                    onVisibleChange: (visible) => setPreviewOpen(visible),
                    afterOpenChange: (visible) => !visible && setPreviewImage(""),
                  }}
                  src={previewImage}
                />
              )}
            </div>

            <div className="mt-10">
              <LoadableButton
                className="bg-primary text-white text-base font-semibold rounded-lg px-12 py-2 themeHover duration-500"
                disabled={isLoading}
                type="button"
                lable="Send Request"
                isLoading={isLoading}
                loadingLable="Sending Request..."
                onClick={handleFormSubmitClick}
              />
            </div>
          </Form>
        </div>

        <div>
          <img src="/assets/images/kyc.png" alt="" className="w-[35rem] p-4" />
        </div>
      </div>

      {/* Signature Modal */}
      <ModalComponent
        isOpen={signatureModalOpen}
        setIsOpen={(val) => {
          setSignatureModalOpen(val);
          if (!val) {
            clearSignatureState();
            setPendingFormValues(null);
            setSignatureTab("write");
          }
        }}
        title="Provide Your Signature"
        width={550}
      >
        <Tabs
            activeKey={signatureTab}
            onChange={handleTabChange}
            items={[
              {
                key: "write",
                label: "Draw Signature",
                children: (
                  <div className="p-4">
                    <div
                      style={{
                        border: "2px dashed #d9d9d9",
                        borderRadius: 8,
                        background: "#fafafa",
                        padding: 8,
                      }}
                    >
                      <SignatureCanvas
                        ref={sigCanvasRef}
                        penColor="black"
                        canvasProps={{
                          width: 490,
                          height: 200,
                          className: "signature-canvas",
                          style: { width: "100%", height: 200, cursor: "crosshair" },
                        }}
                      />
                    </div>
                  </div>
                ),
              },
              {
                key: "upload",
                label: "Upload Signature",
                children: (
                  <div className="ant-upload-drag-wrapper">
                    <Upload
                      beforeUpload={() => false}
                      listType="picture-card"
                      fileList={uploadedSignatureList}
                      onChange={handleSignatureUploadChange}
                      onPreview={handlePreview}
                      maxCount={1}
                    >
                      {uploadedSignatureList.length >= 1 ? null : (
                        <button type="button">
                          <div className="flex justify-center">
                            <img src="/assets/icons/Upload_Document_icon.svg" alt="icon" />
                          </div>
                          Upload
                        </button>
                      )}
                    </Upload>
                  </div>
                ),
              },
            ]}
          />
          <div className="flex justify-end items-center gap-3 mt-4">
            <button
              type="button"
              onClick={handleModalClose}
              className="px-5 py-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 font-medium transition duration-300"
            >
              Cancel
            </button>
            {signatureTab === "write" && (
              <button
                type="button"
                onClick={handleClearSignature}
                className="px-5 py-2 text-white bg-red-500 hover:bg-red-600 rounded-lg font-medium transition duration-300"
              >
                Clear Signature
              </button>
            )}
            <button
              type="button"
              onClick={handleSignatureConfirm}
              className="px-5 py-2 bg-primary text-white rounded-lg font-medium themeHover duration-500"
            >
              Confirm & Submit
            </button>
          </div>
      </ModalComponent>
    </div>
  );
};

export default KycForm;
